import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { PteEventsService } from './pte-events.service';
import { DatePipe } from '@angular/common';
import { AppConstants } from 'src/app/app.constants';
import { LogmanagementService } from '../logmanagement/logmanagement.service';

export interface PteEvent {
  nda_number: string;
  brand_name: string;
  route: string;
  dosage: string;
  patent_number: string;
  patent_expiration: string;
  patent_type: string;
  official_date_dt: string;
  official_date_display: string;
  document_description: string;
  s3_url: string;
}

@Component({
  selector: 'app-pte-events',
  templateUrl: './pte-events.component.html',
  styleUrls: ['./pte-events.component.scss'],
  providers: [DatePipe]
})
export class PteEventsComponent implements OnInit {

  dropdownType: 'today' | 'history' = 'today';
  startDate?: string;
  endDate?: string;

  events: PteEvent[] = [];
  filteredEvents: PteEvent[] = [];
  isLoading = false;

  searchText: string = '';
  page = 1;
  pageSize = 10;
  totalRecords = 0;
  pageSizeOptions = [10, 25, 50, 100];

  insertedStartDate?: string;
  insertedEndDate?: string;

  isDsTimeDialogVisible: boolean = false;
  dsTimeTableRows: { section: string; start_time: string; end_time: string; message: string }[] = [];
  LmsDsTime: any;

  constructor(
    private pteEventsService: PteEventsService,
    private toastr: ToastrService,
    private datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    this.loadEvents();
  }

  /** Total number of pages based on records and page size */
  get totalPages(): number {
    if (this.totalRecords === 0) return 1;
    return Math.ceil(this.totalRecords / this.pageSize);
  }

  get pageNumbers(): number[] {
    const total = this.totalPages;
    const current = this.page;
    const window = 2;

    const start = Math.max(1, current - window);
    const end = Math.min(total, current + window);

    const pages: number[] = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  /** First record index shown on current page (e.g. "Showing 11 to 20") */
  get fromRecord(): number {
    if (this.totalRecords === 0) return 0;
    return (this.page - 1) * this.pageSize + 1;
  }

  /** Last record index shown on current page */
  get toRecord(): number {
    const lastOnPage = this.page * this.pageSize;
    if (lastOnPage > this.totalRecords) return this.totalRecords;
    return lastOnPage;
  }

  loadEvents(): void {
    this.isLoading = true;

    const params: any = {
      dropdown_type: this.dropdownType,
      page: this.page,
      page_size: this.pageSize,
      user_email: localStorage.getItem('email') || ''
    };

    if (this.searchText?.trim()) {
      params.search = this.searchText.trim();
    }

    if (this.startDate) {
      params.start_date = this.datePipe.transform(
        new Date(this.startDate),
        'MM/dd/yyyy'
      );
    }

    if (this.endDate) {
      params.end_date = this.datePipe.transform(
        new Date(this.endDate),
        'MM/dd/yyyy'
      );
    }

    if (this.dropdownType === 'today') {
      if (this.insertedStartDate) {
        params.inserted_start_date = this.datePipe.transform(
          new Date(this.insertedStartDate), 'MM/dd/yyyy'
        );
      }
      if (this.insertedEndDate) {
        params.inserted_end_date = this.datePipe.transform(
          new Date(this.insertedEndDate), 'MM/dd/yyyy'
        );
      }
    }

    this.pteEventsService.getPteEvents(params).subscribe({
      next: (res) => {
        this.events = res.data || [];
        this.filteredEvents = [...this.events];
        this.totalRecords = res.total ?? res.total_count ?? this.events.length;
      },
      error: () => {
        this.toastr.error('Failed to load PTE events');
        this.events = [];
        this.filteredEvents = [];
        this.totalRecords = 0;
      },
      complete: () => this.isLoading = false
    });
  }

  applySearch(): void {
    this.page = 1;
    this.loadEvents();
  }

  resetFilters(): void {
    this.dropdownType = 'today';
    this.startDate = undefined;
    this.endDate = undefined;
    this.page = 1;
    this.searchText = "";
    this.insertedStartDate = undefined;
    this.insertedEndDate = undefined;
    this.loadEvents();
  }

  onDropdownChange(): void {
    this.startDate = undefined;
    this.endDate = undefined;
    this.page = 1;
    this.insertedStartDate = undefined;
    this.insertedEndDate = undefined;
    this.loadEvents();
  }

  goToPage(pageNum: number): void {
    const isValidPage = pageNum >= 1 && pageNum <= this.totalPages;
    if (isValidPage) {
      this.page = pageNum;
      this.loadEvents();
    }
  }

  onPageSizeChange(): void {
    this.page = 1;
    this.loadEvents();
  }

  openDescription(description: string): void {
    alert(description);
  }

  openPtePdf(pdfUrl: string): void {
    window.open(`${AppConstants.API_ENDPOINT}${AppConstants.API.PTE_EVENTS.GET_PTE_PDF}/?pdf_url=${pdfUrl}`, '_blank');

    this.pteEventsService.getPtePdf({ pdf_url: pdfUrl }).subscribe({
      next: () => {
        this.toastr.success('PTE PDF accessed successfully');
      },
      error: () => {
        this.toastr.error('Failed to log PTE PDF access');
      }
    });
  }

  getLmsDsTime() {
    this.pteEventsService.getDSTime().subscribe((response) => {
      this.LmsDsTime = response['data'];
      this.dsTimeTableRows = [];

      const firstItem = this.LmsDsTime[0];

      const lmsdsData = firstItem?.pte_events;
      this.dsTimeTableRows.push({
        section: 'pte_events',
        start_time: lmsdsData?.start_time ?? 'N/A',
        end_time: lmsdsData?.end_time ?? 'N/A',
        message: lmsdsData?.message ?? 'N/A'
      });
    });
  }

  openLastRefreshDialog(): void {
    this.isDsTimeDialogVisible = true;
  }

  closeLastRefreshDialog(): void {
    this.isDsTimeDialogVisible = false;
  }
}
